import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCustomerById, updateTaskStatus, deleteTask, updateCustomer, getTasksByCustomerId, getCustomerOpportunities } from '../services/api';
import { ConfirmModal, CustomSelect, RowActions, RowAction, StatusDot, Amount } from '@redpanda/ui';
import { priorityTone, stageTone, toneOf } from '../utils/statusTone';
import { formatTableDate, listReturnUrl, RESTORE_SCROLL_STATE } from '@redpanda/utils';
import { canModify, canAdminDelete } from '../utils/permissions';
import { UserContext } from '../context/UserContext';
import { useTaskStore, useTaskStoreBase } from '../stores/taskStore';
import { removeItemsWhere } from '../stores/storeHelpers';
import { useAllTagsStore, tagsByIds } from '../stores/tagStore';
import { useCustomerStore, type Customer } from '../stores/customerStore';
import { useOpportunityStore } from '../stores/opportunityStore';
import { useStageStore } from '../stores/stageStore';
import OpportunityDialog from '../components/OpportunityDialog';
import TaskDialog from '../components/TaskDialog';
import CustomerDialog from '../components/CustomerDialog';
import CustomerInteractions from '../components/CustomerInteractions';
import { TagCloudSelector } from '../components/forms';
import { toast } from 'react-hot-toast';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { Button } from '@redpanda/ui';
import { createDetailCache } from '../utils/detailCache';

const customerDetailCache = createDetailCache<{ data: Customer; tasks: any[]; opportunities: any[] }>('customerDetailsCache');

const CustomerDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userContext = useContext(UserContext);
  const userRole = userContext?.user?.role;
  const currentUserId = userContext?.user?.id;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // 使用 stores 
  const { opportunities } = useOpportunityStore();
  const { stages, fetchStages } = useStageStore();
  const { tasks } = useTaskStore();
  const { updateCustomer: updateCustomerInStore, getCustomerById: getCustomerFromStore } = useCustomerStore();
  
  // 先顯示快取（上次看這位客戶時存的，或列表 store 裡的），再背景向後端更新；都沒有才顯示載入中
  // 任務與商機依客戶向後端查詢（列表 store 只有目前那一頁，拿它過濾會漏掉其他頁的資料）
  const initialData = (customerId?: string) => {
    const cached = customerDetailCache.get(customerId);
    const storeTasks = tasks.filter(task => task.customerId === customerId);
    const storeOpportunities = opportunities.filter(opp => opp.customerId === customerId);
    return {
      customer: cached?.data ?? (customerId ? getCustomerFromStore(customerId) : undefined) ?? null,
      tasks: cached?.tasks ?? storeTasks,
      opportunities: cached?.opportunities ?? storeOpportunities,
      tasksLoading: !cached?.tasks && storeTasks.length === 0,
      opportunitiesLoading: !cached?.opportunities && storeOpportunities.length === 0,
    };
  };
  const [initial] = useState(() => initialData(id));
  const [customer, setCustomer] = useState<Customer | null>(initial.customer);
  const [loading, setLoading] = useState(!initial.customer); // 沒有任何資料時才轉圈（一開始就設好，不會先閃「找不到客戶」）
  const [customerOpportunities, setCustomerOpportunities] = useState<any[]>(initial.opportunities);
  const [customerTasks, setCustomerTasks] = useState<any[]>(initial.tasks);
  const [tasksLoading, setTasksLoading] = useState(initial.tasksLoading);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(initial.opportunitiesLoading);

  // 換客戶時（同一個頁面元件、只換網址的 :id）在 render 中直接重設，不會先閃一下上一位客戶的資料
  // （React 官方建議的「依 props 調整 state」寫法，比 useEffect 少一次錯誤畫面）
  const [shownCustomerId, setShownCustomerId] = useState(id);
  if (shownCustomerId !== id) {
    setShownCustomerId(id);
    const next = initialData(id);
    setCustomer(next.customer);
    setLoading(!next.customer);
    setCustomerOpportunities(next.opportunities);
    setCustomerTasks(next.tasks);
    setOpportunitiesLoading(next.opportunitiesLoading);
    setTasksLoading(next.tasksLoading);
  }
  // 目前顯示的客戶：非同步查詢回來時比對，丟掉上一位客戶晚到的回應
  const currentIdRef = useRef(id);
  currentIdRef.current = id;
  const [error, setError] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; taskId: string | null }>({
    isOpen: false,
    taskId: null
  });
  const [showAddOpportunityDialog, setShowAddOpportunityDialog] = useState(false);
  const [showEditOpportunityDialog, setShowEditOpportunityDialog] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<any>(null);
  const [showAddTaskDialog, setShowAddTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [showEditCustomerDialog, setShowEditCustomerDialog] = useState(false);
  const tags = useAllTagsStore(state => state.allTags);
  
  // 標籤管理狀態 - 避免初始化時觸發異常通知
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isTagsInitialized, setIsTagsInitialized] = useState(false);
  
  // 設置客戶標籤（標籤可能是物件陣列或 ID 陣列）
  const applyCustomerTags = (customerData: any) => {
    const tagIds = Array.isArray(customerData?.tags)
      ? customerData.tags
          .map((tag: any) => (typeof tag === 'object' && tag !== null && 'id' in tag ? tag.id : tag))
          .filter((tagId: string) => tagId)
      : [];
    setSelectedTagIds(tagIds);
    setIsTagsInitialized(true);
  };

  // 載入客戶資料
  useEffect(() => {
    const loadCustomer = async () => {
      if (!id) {
        navigate('/customers');
        return;
      }

      // 有快取或列表 store 的資料就先顯示（不轉圈），一律背景向後端更新
      const shown = customerDetailCache.get(id)?.data ?? getCustomerFromStore(id);
      if (shown) {
        setCustomer(shown);
        applyCustomerTags(shown);
      } else {
        setLoading(true);
      }

      try {
        const response = await getCustomerById(id);
        if (currentIdRef.current !== id) return; // 已換到別的客戶，丟掉這筆晚到的回應
        setCustomer(response.data);
        applyCustomerTags(response.data);
        customerDetailCache.set(id, { data: response.data });
      } catch (error: any) {
        if (currentIdRef.current !== id) return; // 上一位客戶的查詢失敗，不在新客戶頁面提示
        if (shown) {
          console.warn('Failed to refresh customer:', error); // 背景更新失敗時保留已顯示的資料
          return;
        }
        console.error(t('customers.detail.loadCustomerFailed', { ns: 'pages' }), error);
        setError(t('customers.detail.loadCustomerFailed', { ns: 'pages' }));
        toast.error(t('customers.detail.loadCustomerFailed', { ns: 'pages' }));
      } finally {
        if (currentIdRef.current === id) setLoading(false);
      }
    };

    loadCustomer();
  }, [id, navigate, getCustomerFromStore]);

  // 手動清理無效的標籤 ID，只在標籤庫變化且初始化完成後進行
  useEffect(() => {
    if (isTagsInitialized && tags.length > 0 && selectedTagIds.length > 0) {
      const validTagIds = selectedTagIds.filter(tagId => tags.find(t => t.id === tagId));
      // 只在確實有無效標籤時才更新，避免不必要的重新渲染
      if (validTagIds.length !== selectedTagIds.length && validTagIds.length < selectedTagIds.length) {
        setSelectedTagIds(validTagIds);
      }
    }
  }, [tags, selectedTagIds, isTagsInitialized]);

  // 載入客戶任務（依客戶查詢，不受任務列表分頁影響）
  const loadCustomerTasks = useCallback(async () => {
    if (!id) return;
    try {
      const response = await getTasksByCustomerId(id);
      if (currentIdRef.current !== id) return;
      setCustomerTasks(response.data);
      customerDetailCache.set(id, { tasks: response.data });
    } catch (error: any) {
      console.error(t('customers.detail.loadTasksFailed', { ns: 'pages' }), error);
    } finally {
      if (currentIdRef.current === id) setTasksLoading(false);
    }
  }, [id, t]);

  // 載入客戶商機（依客戶查詢，不受商機列表分頁影響）
  const loadCustomerOpportunities = useCallback(async () => {
    if (!id) return;
    try {
      const response = await getCustomerOpportunities(id);
      if (currentIdRef.current !== id) return;
      setCustomerOpportunities(response.data);
      customerDetailCache.set(id, { opportunities: response.data });
    } catch (error: any) {
      console.error(t('customers.detail.loadOpportunitiesFailed', { ns: 'pages' }), error);
    } finally {
      if (currentIdRef.current === id) setOpportunitiesLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadCustomerTasks();
  }, [loadCustomerTasks]);

  useEffect(() => {
    loadCustomerOpportunities();
    fetchStages(); // 階段名稱與機率（store 有快取）
  }, [loadCustomerOpportunities, fetchStages]);

  // 對話框是樂觀更新（關閉時 API 還沒完成），API 成功後才會通知，收到再重抓
  useDataChanged('tasks', loadCustomerTasks);
  useDataChanged('opportunities', loadCustomerOpportunities);
  // 編輯客戶對話框也是樂觀更新：API 成功後重抓這位客戶（含標籤）
  useDataChanged('customers', async () => {
    if (!id) return;
    try {
      const response = await getCustomerById(id);
      if (currentIdRef.current !== id) return;
      setCustomer(response.data);
      applyCustomerTags(response.data);
      customerDetailCache.set(id, { data: response.data });
    } catch (error) {
      console.error(t('customers.detail.loadCustomerFailed', { ns: 'pages' }), error);
    }
  });


  const handleStatusChange = async (taskId: string, newStatus: string) => {
    // 保存原始狀態以便回滾
    const originalItems = useTaskStoreBase.getState().items;

    // 樂觀更新：立即更新 UI
    const optimisticTasks = originalItems.map(task =>
      task.id === taskId ? { ...task, status: newStatus } : task
    );
    useTaskStoreBase.setState({ items: optimisticTasks });
    const originalCustomerTasks = customerTasks;
    setCustomerTasks(prev => prev.map(task => (task.id === taskId ? { ...task, status: newStatus } : task)));

    try {
      await updateTaskStatus(taskId, newStatus);
      notifyDataChanged('tasks');

      toast.success(t('customers.detail.taskStatusUpdated', { ns: 'pages' }));
    } catch (error: any) {
      // 失敗時回滾到原始狀態
      console.error(t('customers.detail.taskUpdateFailed', { ns: 'pages' }), error);
      useTaskStoreBase.setState({ items: originalItems });
      setCustomerTasks(originalCustomerTasks);
      toast.error(t('customers.detail.taskUpdateFailed', { ns: 'pages' }));
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteModal.taskId) return;

    try {
      await deleteTask(deleteModal.taskId);
      
      setCustomerTasks(prev => prev.filter(task => task.id !== deleteModal.taskId));
      removeItemsWhere<{ id: string }>(useTaskStoreBase, task => task.id === deleteModal.taskId);
      notifyDataChanged('tasks');
      
      toast.success(t('customers.detail.taskDeleted', { ns: 'pages' }));
      setDeleteModal({ isOpen: false, taskId: null });
    } catch (error: any) {
      console.error(t('customers.detail.taskDeleteFailed', { ns: 'pages' }), error);
      toast.error(t('customers.detail.taskDeleteFailed', { ns: 'pages' }));
    }
  };

  const formatDate = (dateString: string) => formatTableDate(dateString, i18n.language);

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'HIGH': return t('tasks.priority.HIGH', { ns: 'pages' });
      case 'MEDIUM': return t('tasks.priority.MEDIUM', { ns: 'pages' });
      case 'LOW': return t('tasks.priority.LOW', { ns: 'pages' });
      default: return priority;
    }
  };

  // 獲取階段名稱
  const getStageName = (stageId: string) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.name || t('customers.detail.unknownStage', { ns: 'pages' });
  };

  // 獲取階段機率
  const getStageProbability = (stageId: string) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.probability || 0;
  };

  // 逾期：到期日早於今天（今天到期不算逾期，與儀表板、任務列表一致）
  const isOverdue = (dueDate: string, status: string) => {
    if (status === 'COMPLETED') return false;
    const due = new Date(dueDate);
    const today = new Date();
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return due < today;
  };

  // 標籤管理功能 - 直接保存變更
  const handleTagsChange = useCallback(async (newTagIds: string[]) => {
    if (!id) return;

    // 如果還沒有初始化完成，只更新本地狀態，不顯示 Toast 也不調用 API
    if (!isTagsInitialized) {
      setSelectedTagIds(newTagIds);
      return;
    }

    // 保存原來的標籤以便錯誤回滾
    const originalTagIds = selectedTagIds;
    const originalCustomer = customer;
    
    try {
      // 樂觀更新本地狀態
      setSelectedTagIds(newTagIds);
      
      // 樂觀更新客戶對象
      if (customer) {
        const updatedCustomer = { ...customer, tags: tagsByIds(newTagIds) };
        setCustomer(updatedCustomer);
        updateCustomerInStore(id, updatedCustomer);
        customerDetailCache.set(id, { data: updatedCustomer }); // 快取也要同步，否則重進頁面會先閃一下舊標籤
      }
      
      toast.success(t('customers.detail.tags.updateSuccess', { ns: 'pages' }));
      
      // 背景執行API更新
      await updateCustomer(id, { tags: newTagIds });
      notifyDataChanged('customers');
      
    } catch (error: any) {
      console.error('Tag update failed:', error);
      
      // 失敗時恢復原狀態
      setSelectedTagIds(originalTagIds);
      if (originalCustomer) {
        setCustomer(originalCustomer);
        updateCustomerInStore(id, originalCustomer);
        customerDetailCache.set(id, { data: originalCustomer });
      }
      
      toast.error(t('customers.detail.tags.updateError', { ns: 'pages' }));
    }
  }, [id, isTagsInitialized, selectedTagIds, updateCustomer, getCustomerById, setCustomer, updateCustomerInStore, t]);

  // 處理編輯商機
  const handleEditOpportunity = (opportunity: any) => {
    setEditingOpportunity(opportunity);
    setShowEditOpportunityDialog(true);
  };

  // 關閉對話框
  const handleCloseOpportunityDialogs = () => {
    setShowAddOpportunityDialog(false);
    setShowEditOpportunityDialog(false);
    setEditingOpportunity(null);
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-8">
        {error || t('common.messages.notAvailable')}
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center text-red-600 p-8">
        {t('customers.detail.notFound', { ns: 'pages' })}
      </div>
    );
  }

  // 左欄「標籤：值」（§3-13）
  const facts = [
    { label: t('common.forms.labels.company'), value: customer.company },
    { label: t('common.forms.labels.address'), value: customer.address },
    { label: t('common.table.columns.createdAt'), value: formatDate(customer.createdAt) },
  ];

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      {/* 頁首：麵包屑＋客戶名稱（頁標題）＋聯絡資訊＋1 顆主要按鈕（§3-13） */}
      <nav className="mb-1 text-[13px] text-fg-secondary">
        <Link to={listReturnUrl('/customers')} state={RESTORE_SCROLL_STATE} className="hover:text-fg transition-colors">{t('customers.list.title', { ns: 'pages' })}</Link>
        <span className="mx-1.5 text-fg-muted">/</span>
      </nav>
      <div className="mb-4">
        <div className="flex items-baseline justify-between gap-4">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white truncate">{customer.name}</h1>
          {canModify(customer, userRole, currentUserId) && (
            <Button onClick={() => setShowEditCustomerDialog(true)}>
              {t('customers.detail.editCustomer', { ns: 'pages' })}
            </Button>
          )}
        </div>
        {(customer.email || customer.phone) && (
          <p className="mt-1 text-sm text-fg-secondary">
            {customer.email && <a href={`mailto:${customer.email}`} className="hover:text-accent transition-colors">{customer.email}</a>}
            {customer.email && customer.phone && <span className="mx-2 text-fg-muted">·</span>}
            {customer.phone && <a href={`tel:${customer.phone}`} className="hover:text-accent transition-colors">{customer.phone}</a>}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左欄：基本資料＋標籤，同一個容器 */}
        <div className="lg:col-span-1 self-start bg-surface rounded-lg border border-line divide-y divide-line">
          <dl className="divide-y divide-line text-sm">
            {facts.map(f => (
              <div key={f.label} className="grid grid-cols-[5rem_1fr] gap-3 px-5 py-3">
                <dt className="text-fg-secondary">{f.label}</dt>
                <dd className={f.value ? 'text-fg break-words' : 'text-fg-muted'}>{f.value || '—'}</dd>
              </div>
            ))}
          </dl>
          <section className="px-5 py-4">
            <h2 className="text-sm font-semibold text-fg mb-3">{t('customers.detail.tags.title', { ns: 'pages' })}</h2>
            <TagCloudSelector value={selectedTagIds} onChange={handleTagsChange} />
          </section>
        </div>

        {/* 右側：任務與商機，各自一個容器、清單用細線分隔（R13） */}
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-surface rounded-lg border border-line">
            <div className="flex items-baseline justify-between px-5 pt-4 pb-3">
              <h2 className="text-base font-semibold text-fg">
                {t('customers.detail.relatedTasks', { count: customerTasks.length, ns: 'pages' })}
              </h2>
              <button onClick={() => setShowAddTaskDialog(true)} className="text-sm font-medium text-accent hover:underline">
                {t('customers.detail.addTask', { ns: 'pages' })}
              </button>
            </div>
            {tasksLoading ? (
              <p className="px-5 pb-5 text-sm text-fg-secondary">{t('common.status.loading')}</p>
            ) : customerTasks.length === 0 ? (
              <p className="px-5 pb-5 text-sm text-fg-secondary">{t('customers.detail.noTasks', { ns: 'pages' })}</p>
            ) : (
              <ul className="border-t border-line divide-y divide-line">
                {customerTasks.map((task) => {
                  const overdue = isOverdue(task.dueDate, task.status);
                  return (
                    <li key={task.id} className="px-5 py-3 flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <p className="text-sm font-medium text-fg truncate">{task.title}</p>
                          <StatusDot tone={toneOf(priorityTone, task.priority)} label={getPriorityText(task.priority)} />
                        </div>
                        {task.description && <p className="mt-0.5 text-[13px] text-fg-secondary line-clamp-2">{task.description}</p>}
                        <p className="mt-1 text-[13px] text-fg-secondary tabular-nums">
                          <span className={overdue ? 'text-red-600 dark:text-red-400' : ''}>
                            {t('customers.detail.taskDueDate', { date: formatDate(task.dueDate), ns: 'pages' })}
                            {overdue && ` ${t('customers.detail.overdue', { ns: 'pages' })}`}
                          </span>
                          {task.createdByName && (
                            <>
                              <span className="mx-1.5 text-fg-muted">·</span>
                              {t('customers.detail.createdBy', { name: task.createdByName, ns: 'pages' })}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="w-28">
                          <CustomSelect
                            value={task.status}
                            onChange={(value) => handleStatusChange(task.id, value)}
                            options={[
                              { value: 'PENDING', label: t('tasks.status.PENDING', { ns: 'pages' }) },
                              { value: 'COMPLETED', label: t('tasks.status.COMPLETED', { ns: 'pages' }) },
                            ]}
                            className="text-sm"
                          />
                        </div>
                        {/* 權限與任務列表相同（原本「編輯」連到 /tasks，改就地編輯時要保留權限檢查） */}
                        <RowActions>
                          {canModify(task, userRole, currentUserId) && (
                            <RowAction onClick={() => setEditingTask(task)}>{t('common.actions.edit')}</RowAction>
                          )}
                          {canAdminDelete(task, userRole) && (
                            <RowAction tone="danger" onClick={() => setDeleteModal({ isOpen: true, taskId: task.id })}>{t('common.actions.delete')}</RowAction>
                          )}
                        </RowActions>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="bg-surface rounded-lg border border-line">
            <div className="flex items-baseline justify-between px-5 pt-4 pb-3">
              <h2 className="text-base font-semibold text-fg">
                {t('customers.detail.relatedOpportunities', { count: customerOpportunities.length, ns: 'pages' })}
              </h2>
              <button onClick={() => setShowAddOpportunityDialog(true)} className="text-sm font-medium text-accent hover:underline">
                {t('customers.detail.addOpportunity', { ns: 'pages' })}
              </button>
            </div>
            {opportunitiesLoading ? (
              <p className="px-5 pb-5 text-sm text-fg-secondary">{t('common.status.loading')}</p>
            ) : customerOpportunities.length === 0 ? (
              <p className="px-5 pb-5 text-sm text-fg-secondary">{t('customers.detail.noOpportunities', { ns: 'pages' })}</p>
            ) : (
              <ul className="border-t border-line divide-y divide-line">
                {customerOpportunities.map((opportunity) => (
                  <li key={opportunity.id} className="px-5 py-3 flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <Link to={`/opportunities/${opportunity.id}`} className="text-sm font-medium text-fg truncate hover:text-accent transition-colors">
                          {opportunity.title}
                        </Link>
                        <StatusDot tone={stageTone(getStageProbability(opportunity.currentStageId))} label={getStageName(opportunity.currentStageId)} />
                      </div>
                      <p className="mt-1 text-[13px] text-fg-secondary tabular-nums">
                        {t('customers.detail.probability', { percent: Math.round(getStageProbability(opportunity.currentStageId) * 100), ns: 'pages' })}
                        {opportunity.createdByName && (
                          <>
                            <span className="mx-1.5 text-fg-muted">·</span>
                            {t('customers.detail.owner', { name: opportunity.createdByName, ns: 'pages' })}
                          </>
                        )}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-medium text-fg tabular-nums"><Amount value={opportunity.value} /></span>
                    {canModify(opportunity, userRole, currentUserId) && (
                      <RowActions className="shrink-0">
                        <RowAction onClick={() => handleEditOpportunity(opportunity)}>{t('common.actions.edit')}</RowAction>
                      </RowActions>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* 互動記錄（原本只在「編輯客戶」頁） */}
          <CustomerInteractions customer={customer} currentUserId={currentUserId} currentUserRole={userRole} />
        </div>
      </div>

      <CustomerDialog
        isOpen={showEditCustomerDialog}
        onClose={() => setShowEditCustomerDialog(false)}
        customer={customer}
      />

      {/* 刪除確認對話框 */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, taskId: null })}
        onConfirm={handleDeleteTask}
        title={t('common.messages.confirmDelete')}
        message={t('tasks.list.confirmDeleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        t={t}
      />

      {/* 新增商機對話框 */}
      <OpportunityDialog
        isOpen={showAddOpportunityDialog}
        onClose={handleCloseOpportunityDialogs}
        preselectedCustomerId={customer?.id}
      />

      {/* 編輯商機對話框 */}
      <OpportunityDialog
        isOpen={showEditOpportunityDialog}
        onClose={handleCloseOpportunityDialogs}
        opportunity={editingOpportunity}
      />

      {/* 新增任務對話框 */}
      <TaskDialog
        isOpen={showAddTaskDialog}
        onClose={() => setShowAddTaskDialog(false)}
        preselectedCustomerId={customer?.id}
      />

      {/* 編輯任務對話框（原本「編輯」連到任務列表頁，改為就地編輯） */}
      <TaskDialog
        isOpen={!!editingTask}
        onClose={() => setEditingTask(null)}
        task={editingTask}
      />
    </div>
  );
};

export default CustomerDetailPage;